'use client';
import React, { useEffect, useState, useRef } from 'react';
import './Index.css';
import { Box, Paper, Stack, Typography, SortDirection } from '@mui/material';
import Constants from '../../utils/constants';
import HttpService from '../../services/HttpService';
import { useStore } from '../../utils/store';
import CustomPaginationActionsTable from '../../components/TablePagination';
import StatusRequestData from '../../Models/StatusRequest';
import { useSearch } from '../../components/searchContext';
import AlertMessageComponent from '../../components/AlertMessageComponent';
import { useAlert } from '../../components/alertContext';
import ButtonComponent from '../../components/form/ButtonComponent';
import SelectFieldComponent from '../../components/form/SelectFieldComponent';
import SimpleDialogComponent from '../../components/SimpleDialogComponent';
import TextFieldComponent from '../../components/form/TextFieldComponent';
import { formatPrice, normalizeDecimalBlur } from '../../utils/storageUtil';

// Allow an optional leading minus, digits, and a single decimal point with
// up to 2 decimal places (e.g. "-12.50", "8.25").
const sanitizeDecimal = (raw: any): string => {
  const isNegative = String(raw ?? '').startsWith('-');
  let v = String(raw ?? '').replace(/[^0-9.]/g, '');
  const firstDot = v.indexOf('.');
  if (firstDot !== -1) {
    v = v.slice(0, firstDot + 1) + v.slice(firstDot + 1).replace(/\./g, '');
    const [intPart, decPart] = v.split('.');
    v = intPart + '.' + decPart.slice(0, 2);
  }
  return (isNegative ? '-' : '') + v;
};

const EndOfMonth: React.FC = () => {
  const isInitialLoad = useRef(true);
  const { getAdmin } = useStore();
  const { searchQuery } = useSearch();

  const monthsArr = [
    { id: 0, label: 'January' },
    { id: 1, label: 'February' },
    { id: 2, label: 'March' },
    { id: 3, label: 'April' },
    { id: 4, label: 'May' },
    { id: 5, label: 'June' },
    { id: 6, label: 'July' },
    { id: 7, label: 'August' },
    { id: 8, label: 'September' },
    { id: 9, label: 'October' },
    { id: 10, label: 'November' },
    { id: 11, label: 'December' },
  ];
  // Table Columns Definition
  const columns: any[] = [
    {
      field: 'name',
      headerName: 'Name',
    },

    {
      field: 'unitType',
      headerName: 'Unit of Measure',
      renderCell: (value: any) => {
        return <Box>{value ? value.unitName : '-'}</Box>;
      },
    },

    {
      field: 'stockLevel',
      headerName: 'Stock Level',
      valueGetter: (value: any) => formatPrice(value),
    },

    {
      field: 'actualStockLevel',
      headerName: 'Actual Amount',
      renderCell: (value: any, row: any) => (
        <Box sx={{ width: 100 }}>
          {row.eomStatus === 'completed' ? (
            formatPrice(value)
          ) : (
            <TextFieldComponent
              errorStr=""
              type="text"
              isRequired={false}
              keyName={row.resourceId + '_transferQty'}
              label=""
              name={row.resourceId + '_transferQty'}
              value={value ?? ''}
              onChange={(event: any) => {
                const newValue = sanitizeDecimal(event.target.value);
                const currentIndex = rows.findIndex(
                  (item) => item.resourceId === row.resourceId
                );
                setRows((prevItems) => {
                  const updatedItems = [...prevItems];
                  if (currentIndex !== -1) {
                    updatedItems[currentIndex]['actualStockLevel'] = newValue;
                  }
                  return updatedItems;
                });
              }}
              onBlur={() => {
                const currentIndex = rows.findIndex(
                  (item) => item.resourceId === row.resourceId
                );
                setRows((prevItems) => {
                  const updatedItems = [...prevItems];
                  if (currentIndex !== -1) {
                    updatedItems[currentIndex]['actualStockLevel'] =
                      normalizeDecimalBlur(
                        updatedItems[currentIndex]['actualStockLevel']
                      );
                  }
                  return updatedItems;
                });
              }}
            />
          )}
        </Box>
      ),
    },
  ];
  const ReasonColumns: any[] = [
    {
      field: 'name',
      headerName: 'Name',
    },
    {
      field: 'stockLevel',
      headerName: 'Stock Level',
      valueGetter: (value: any) => formatPrice(value),
    },
    {
      field: 'actualStockLevel',
      headerName: 'Actual Amount',
      valueGetter: (value: any) => formatPrice(value),
    },
    {
      field: 'id',
      headerName: 'Discrepancy',
      renderCell: (value: any, row: any) => (
        <span>
          {formatPrice(Number(row.actualStockLevel) - Number(row.stockLevel))}
        </span>
      ),
    },
    {
      field: 'reasonId',
      headerName: 'Reason Code',
      renderCell: (value: any, row: any) => (
        <Box>
          <SelectFieldComponent
            errorStr=""
            isRequired={false}
            keyName={value + 'reason_src'}
            label=""
            name={value + 'reason_src'}
            value={row.reasonId}
            onChange={(event: any) => {
              const newValue = event.target.value;
              const currentIndex = unmatchList.findIndex(
                (item) => item.resourceId === row.resourceId
              );
              //  console.log(transferList[currentIndex], '-------');
              setUnmatchList((prevItems) => {
                const updatedItems = [...prevItems];
                if (currentIndex !== -1) {
                  updatedItems[currentIndex]['reasonId'] = newValue;
                }
                return updatedItems;
              });
            }}
            items={reasonsList}
          />
        </Box>
      ),
    },
  ];

  const { alert, setAlert, handleCloseAlert } = useAlert();
  const [totalRecords, setTotalRecords] = useState(0);
  const [submissioType, setSubmissionType] = useState<any>('');
  const [locationList, setLocationList] = useState<any[]>([]);
  const [saveInProgress, setSaveInProgress] = useState<boolean>(false);
  const [currentLocation, setCurrentLocation] = useState<any>(0);
  const [itemsNotSubmitted, setItemsNotSubmitted] = useState<any>([]);
  const [currentYear, setCurrentYear] = useState<any>(new Date().getFullYear());

  const [unmatchList, setUnmatchList] = useState<any[]>([]);
  const [reasonsList, setReasonsList] = useState<any[]>([]);
  const [currentMonth, setCurrentMonth] = useState<any>(new Date().getMonth());
  const [ReasonDialogViewOpen, setReasonDialogViewOpen] =
    useState<boolean>(false);
  const [confirmDailogOpen, setConfirmDailogOpen] = useState<boolean>(false);

  const [rows, setRows] = useState<any[]>([]);
  const [orderBy, setOrderBy] = useState<any>('id');
  const [order, setOrder] = useState<SortDirection>('asc');
  const handleOrderBy = (orderByColumn: any) => {
    let columnName: any = orderByColumn;
    if (orderByColumn === 'unitType') columnName = 'unitType.unitName';
    console.log(columnName);
    setOrderBy(columnName);
    // console.log(order);
    // console.log(orderByColumn);
    if (order === 'desc') {
      setOrder('asc');
      return;
    } else {
      setOrder('desc');
      return;
    }
  };
  const [paginate, setPaginate] = useState({
    limit: Constants.DEFAULT_PAGE_SIZE,
    offset: 0,
  });

  useEffect(() => {
    if (isInitialLoad.current) {
      if (locationList.length === 0) {
        fetchLocations();
      }
      if (reasonsList.length === 0) {
        fetchBundleData();
      }

      isInitialLoad.current = false;
    }
    if (currentLocation !== 0) {
      const locationData = getLocationAndType(currentLocation);
      fetchData(locationData.location, locationData.type);
    }
  }, [searchQuery, paginate, currentLocation, currentMonth, currentYear]);

  const fetchLocations = async () => {
    try {
      const response = await HttpService.get(
        `${Constants.apiURL + Constants.inventoryURL}/locations/source`,
        { headers: { Authorization: `Bearer ${getAdmin()}` } },
        { search: '', limit: -1, offset: 0, isActive: true }
      );
      //console.log(response);
      if (response.data) {
        const locationDataList: any = [
          { id: 0, label: 'Select Location', locationType: 'all' }, // Default option
          ...response.data.locations
            .map((item: any) => ({
              id: item.locationRefId + '-' + item.locationType,
              label: item.locationName,
            }))
            .sort((a: any, b: any) => a.label.localeCompare(b.label)),
        ];
        setLocationList(locationDataList);

        return true;
      } else {
        return false;
      }
    } catch (error: any) {
      console.error(`Error fetching data:`, error);
      return false;
    }
  };
  const getActualMonth = (month: number) => {
    if (month < 10) {
      return '0' + month;
    } else {
      return month;
    }
  };
  // Fetch Data
  const fetchData = async (location: any = null, type: any) => {
    try {
      const searchQueries: any = {
        search: searchQuery,
        limit: paginate.limit,
        offset: paginate.offset,
        locationRefId: location,
        locationType: type,
        monthYear: `${currentYear}/${getActualMonth(currentMonth + 1)}`,
      };

      const response = await HttpService.get(
        `${Constants.apiURL + Constants.inventoryURL}/eom`,
        { headers: { Authorization: `Bearer ${getAdmin()}` } },
        searchQueries
      );
      //console.log(response);
      if (response.data) {
        // Normalize quantity fields to 2 decimals (DECIMAL columns come back
        // as strings like "458.55000") so displays and the editable Actual
        // Amount input show "458.55" instead of "458.55000".
        const normalizedRecords = (response.data.paginateRecords || []).map(
          (item: any) => ({
            ...item,
            stockLevel: formatPrice(item.stockLevel),
            actualStockLevel: formatPrice(item.actualStockLevel),
          })
        );
        setRows(normalizedRecords);
        setTotalRecords(response.data.total);
        handleOrderBy('id');
        setItemsNotSubmitted(
          normalizedRecords.filter(
            (item: any) => item.eomStatus !== 'completed'
          )
        );
        return true;
      } else {
        setRows([]);
        setTotalRecords(0);
        return false;
      }
    } catch (error: any) {
      console.error(`Error fetching data:`, error);
      return false;
    }
  };
  const validateItems = (type: any) => {
    const isActualEntered = (val: any) =>
      val !== undefined &&
      val !== null &&
      String(val).trim() !== '' &&
      String(val).trim() !== '-' &&
      !isNaN(parseInt(val));
    if (type === 'save') {
      const missingPreviouslySubmitted = rows.filter(
        (item: any) =>
          item.inventoryEomCountId &&
          item.eomStatus !== 'completed' &&
          !isActualEntered(item.actualStockLevel)
      );
      if (missingPreviouslySubmitted.length > 0) {
        setAlert({
          type: 'error',
          message:
            'Please enter Actual Amount for previously submitted records',
          open: true,
        });
        return 0;
      }
    }
    const items: any[] = rows.filter(
      (item: any) =>
        isActualEntered(item.actualStockLevel) && item.eomStatus !== 'completed'
    );
    setSubmissionType(type);
    // .map((item: any) => ({
    //   resourceId: item.resourceId,
    //   inventoryItemLocationId: item.inventoryItemLocationId,
    //   monthYear: `${currentYear}/${getActualMonth(currentMonth + 1)}`,
    //   stockLevel: item.stockLevel,
    //   actualStockLevel: item.actualStockLevel,
    // }));
    if (items.length > 0) {
      if (
        items.length !==
          rows.filter((item: any) => item.eomStatus !== 'completed').length &&
        type === 'final'
      ) {
        setAlert({
          type: 'error',
          message: 'all items are required',
          open: true,
        });
      } else {
        if (type === 'final') {
          const itemsUnmatched: any[] = rows.filter(
            (item: any) =>
              Number(item.actualStockLevel) !== Number(item.stockLevel) &&
              item.actualStockLevel &&
              item.eomStatus !== 'completed'
          );
          if (itemsUnmatched.length === 0) {
            setUnmatchList([]);
            setConfirmDailogOpen(true);
          } else {
            setUnmatchList(itemsUnmatched);
            setReasonDialogViewOpen(true);
          }
        } else {
          setUnmatchList([]);
          handleEOM();
          // setConfirmDailogOpen(true);
        }
      }
    } else {
      setUnmatchList([]);
      setAlert({
        type: 'error',
        message: 'There are no items for submit',
        open: true,
      });
    }

    return 0;
  };
  const fetchBundleData = async () => {
    try {
      const response = await HttpService.get(
        `${Constants.apiURL + Constants.refBundleURL}/fetchInventoryReasons`,
        { headers: { Authorization: `Bearer ${getAdmin()}` } },
        null
      );
      //console.log(response);
      if (response.data) {
        setReasonsList(response.data);
      }
    } catch (error: any) {
      console.error(`Error fetching data:`, error);
      setAlert({
        type: 'error',
        message: error.message,
        open: true,
      });
    }
  };
  const handleEOM = async () => {
    try {
      //console.log(unmatchList);
      const items: any[] = rows
        .filter(
          (item: any) =>
            item.actualStockLevel !== undefined &&
            item.actualStockLevel !== null &&
            String(item.actualStockLevel).trim() !== '' &&
            String(item.actualStockLevel).trim() !== '-' &&
            !isNaN(parseInt(item.actualStockLevel)) &&
            item.eomStatus !== 'completed'
        )
        .map((item: any) => ({
          resourceId: item.resourceId,
          inventoryItemLocationId: item.inventoryItemLocationId,
          monthYear: `${currentYear}/${getActualMonth(currentMonth + 1)}`,
          stockLevel: item.stockLevel,
          actualStockLevel: item.actualStockLevel,
          eomStatus: submissioType === 'final' ? 'completed' : 'pending',
          inventoryEomCountId: item.inventoryEomCountId
            ? item.inventoryEomCountId
            : null,
          reasonId:
            unmatchList.length > 0
              ? unmatchList.find(
                  (uItem) => item.resourceId === uItem.resourceId
                )
                ? unmatchList.find(
                    (uItem) => item.resourceId === uItem.resourceId
                  ).reasonId
                : null
              : null,
        }));

      if (items.length === 0) {
        setAlert({
          type: 'error',
          message: 'at least one quantity is required',
          open: true,
        });
        return false;
      }

      setSaveInProgress(true);
      const endpoint = `${Constants.apiURL + Constants.inventoryURL}/eom`;
      const response = await HttpService.post(endpoint, items, {
        headers: { Authorization: `Bearer ${getAdmin()}` },
      });
      setSubmissionType('');
      if (response.success) {
        setAlert({
          type: 'success',
          message: 'Saved successfully',
          open: true,
        });

        window.location.reload();
      }
      setSaveInProgress(false);
      setSubmissionType('');
    } catch (error: any) {
      console.log(error);
      setSubmissionType('');
      setSaveInProgress(false);
    }
  };
  const handlePagination = (paginationData: any) => setPaginate(paginationData);

  const handleStatus = (row: any) => {
    let status = '1';
    if (row.status === '1') {
      status = '0';
    }
    if (row.status === '0') {
      status = '1';
    }
    StatusUpdate({ id: row.id, status: status });
  };

  const StatusUpdate = async (dataObj: StatusRequestData) => {
    try {
      console.log('GET Response:');
    } catch (error: any) {}
  };

  const dialogConfirmation = () => {
    return (
      <Box sx={{ width: '450px' }}>
        <h2>
          Are you sure you close out{' '}
          {monthsArr.find((item) => item.id == currentMonth)?.label}{' '}
          {locationList.find((item) => item.id == currentLocation)?.label}s
        </h2>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'end',
            alignItems: 'end',
            gap: '20px',
          }}
        >
          <ButtonComponent
            errors={[]}
            keyName="okbuttonconfirm"
            label={saveInProgress ? 'Saving...' : 'Close'}
            onClick={() => {
              //  setReasonDialogViewOpen(false);
              handleEOM();
              setConfirmDailogOpen(false);
            }}
          />
        </Box>
      </Box>
    );
  };
  const handleReason = () => {
    const items = unmatchList.filter((item) => item.reasonId > 0);

    if (items.length !== unmatchList.length) {
      setAlert({
        type: 'error',
        message: 'Select Reason from all rows',
        open: true,
      });
    } else {
      setReasonDialogViewOpen(false);
      setConfirmDailogOpen(true);
    }
  };
  const cancelReason = () => {
    setReasonDialogViewOpen(false);
    setUnmatchList([]);
  };
  const ReasonDialogView = () => {
    return (
      <Box sx={{ width: '800px' }}>
        <Typography variant="h6"> Items</Typography>
        <CustomPaginationActionsTable
          rows={unmatchList}
          columns={ReasonColumns}
          totalRecords={0}
          isPagingHide={true}
          handlePagination={() => {}}
          handleRowClick={(row: any) => {}}
          handleStatus={() => {}}
          paginationModel={{
            page: paginate.offset,
            pageSize: paginate.limit,
          }}
        />
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'flex-end',
            justifyContent: 'end',
            m: 2,
          }}
        >
          <ButtonComponent
            onClick={() => {
              cancelReason();
            }}
            label="Cancel"
            color="secondary"
          />
          <ButtonComponent
            onClick={() => {
              handleReason();
            }}
            label="Save & Close"
          />
        </Box>
      </Box>
    );
  };
  const getLocationAndType = (locationData: any) => {
    const locationArr = locationData.split('-');
    if (locationArr.length > 1) {
      return { location: locationArr[0], type: locationArr[1] };
    } else {
      return { location: locationArr[0], type: '' };
    }
  };
  const handleLocation = (id: any) => {
    setCurrentLocation(id);
  };

  return (
    <Stack>
      <Box id="main" className="main dashboardmain">
        <section className="section dashboard">
          <div className="containergrid">
            <Typography variant="h6" sx={{ m: 2 }}>
              End of Month
            </Typography>
            <Paper sx={{ width: '100%' }} className="mt-4">
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: '',
                  alignItems: 'baseline',
                  background: '#f9f9f9',
                  padding: '10px',
                  mb: 2,
                  gap: 2,
                }}
              >
                <Typography>Location</Typography>
                <SelectFieldComponent
                  label=""
                  items={locationList}
                  onChange={(event: any) => {
                    handleLocation(event.target.value);
                  }}
                  isRequired={false}
                  name="filterdrp"
                  value={currentLocation}
                  errorStr=""
                  keyName="filterdrp"
                />
                <Typography>Select Month</Typography>
                <SelectFieldComponent
                  label=""
                  items={monthsArr}
                  onChange={(event) => {
                    setCurrentMonth(event.target.value);
                  }}
                  isRequired={false}
                  name="filterdrpMonth"
                  value={currentMonth}
                  errorStr=""
                  keyName="filterdrpMonth"
                />
                <Typography>Select Year</Typography>
                <SelectFieldComponent
                  label=""
                  items={Array.from({ length: 11 }, (_, i) => {
                    const year = new Date().getFullYear() - 5 + i;
                    return { label: `${year}`, id: year };
                  })}
                  onChange={(event) => {
                    setCurrentYear(event.target.value);
                  }}
                  isRequired={false}
                  name="filterdrpMonth"
                  value={currentYear.toString()}
                  errorStr=""
                  keyName="filterdrpMonth"
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '10px',
                  mb: 2,
                }}
              >
                <Typography variant="h6">All End of Month</Typography>
                {itemsNotSubmitted.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    {rows.length > 0 && (
                      <ButtonComponent
                        onClick={() => {
                          validateItems('save');
                        }}
                        keyName="saveclose"
                        label={saveInProgress ? 'Saving....' : 'Save & Close'}
                      />
                    )}
                    {rows.length > 0 && (
                      <ButtonComponent
                        onClick={() => {
                          validateItems('final');
                        }}
                        keyName="finaliseeom"
                        label="Finalise"
                      />
                    )}
                  </Box>
                )}
              </Box>

              <CustomPaginationActionsTable
                rows={rows}
                columns={columns}
                totalRecords={totalRecords}
                handlePagination={handlePagination}
                handleRowClick={(row: any) => {}}
                handleStatus={handleStatus}
                order={order}
                orderBy={orderBy}
                handleOrderBy={handleOrderBy}
                paginationModel={{
                  page: paginate.offset,
                  pageSize: paginate.limit,
                }}
              />
            </Paper>
          </div>
        </section>
      </Box>
      <AlertMessageComponent
        type={alert.type}
        open={alert.open}
        handleClose={handleCloseAlert}
        message={alert.message}
      />

      <SimpleDialogComponent
        title={'Reason'}
        dialogContent={ReasonDialogView()}
        open={ReasonDialogViewOpen}
        handleClose={cancelReason}
      />
      {
        <SimpleDialogComponent
          title={'Confirmation'}
          dialogContent={dialogConfirmation()}
          open={confirmDailogOpen}
          handleClose={() => {
            setConfirmDailogOpen(false);
          }}
        />
      }
    </Stack>
  );
};

export default EndOfMonth;
